import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { ConfirmationModalComponent, Select } from 'commons/components';
import React, { useMemo } from 'react';
import { usePopupManager } from 'react-popup-manager';
import {
    EapFormContentWrapper,
    EapWorkflowOverlayComponent,
} from '../settings-devices-eap-settings-form/settings-devices-eap-settings-form.style';
import {
    DELIMITER_CHARS,
    EAP_SETTINGS_IMPORT_COLUMN_HEADERS,
} from '../settings-devices-eap-settings-import-modal/settings-devices-eap-settings-import-modal.model';
import { getSingleSelectValue } from '../settings-devices-eap-settings.utils';
import { useEapSettingsImporting } from './hook/use-eap-settings-importing.hook';
import { SettingsDevicesEapSettingsImportingFieldComponent } from './settings-devices-eap-settings-importing-field.component';
import { SettingsDevicesEapSettingsImportingMissingCertificatesModalComponent } from './settings-devices-eap-settings-importing-missing-certificates-modal.component';
import { isValidCertificateFile, UNSAVED_DATA_MODAL_KEYS } from './settings-devices-eap-settings-importing.business';
import { getEapImportFieldDefinitions } from './settings-devices-eap-settings-importing.constants';
import { EapImportFieldDefinition } from './settings-devices-eap-settings-importing.model';
import {
    EapSettingsImportingAbortModal,
    EapSettingsImportingAbortModalDescription,
    EapSettingsImportingAssignRow,
    EapSettingsImportingAssignSection,
    EapSettingsImportingCard,
    EapSettingsImportingConfirmationModalContent,
    EapSettingsImportingTitle,
} from './settings-devices-eap-settings-importing.style';

export {
    buildImportPayloadFromState,
    getCertFilenamesForFieldFromContent,
    getDataRowsFromImportFlow,
    normalizeCipherType,
} from './settings-devices-eap-settings-importing.helpers';
export type { EapImportMappings } from './settings-devices-eap-settings-importing.model';

export const SettingsDevicesEapSettingsImportingContainer: React.FC = () => {
    const { open: popupManagerOpen } = usePopupManager();

    const {
        t,
        importFlowData,
        selectedDevice,
        setSelectedDevice,
        mappings,
        updateMapping,
        isImporting,
        isMissingCertificatesModalOpen,
        isAbortUploadModalOpen,
        setIsAbortUploadModalOpen,
        isConfirmationModalOpen,
        setIsConfirmationModalOpen,
        missingCertificates,
        missingCertificateUploadStatus,
        files,
        onLoadFile,
        onDismissFile,
        onImport,
        onContinue,
        onClose,
        onAbortUpload,
        resetMissingCertificatesFlow,
        setIsMissingCertificatesModalOpen,
    } = useEapSettingsImporting();

    const fieldDefinitions: EapImportFieldDefinition[] = useMemo(() => getEapImportFieldDefinitions(t), [t]);

    const fileColumns = useMemo(() => {
        if (!importFlowData?.fileContent) {
            return [];
        }

        const firstLine =
            importFlowData.fileContent
                .split(/\r?\n/)
                .map((line) => line.trim())
                .find((line) => line.length > 0) ?? '';

        if (!firstLine) {
            return [];
        }

        const delimiter = DELIMITER_CHARS[importFlowData.delimiter];

        return firstLine.split(delimiter).map((item) => item.trim());
    }, [importFlowData]);

    const fileColumnOptions = useMemo(() => {
        if (importFlowData?.columnHeaders === EAP_SETTINGS_IMPORT_COLUMN_HEADERS.WITH_HEADERS) {
            return fileColumns.map((columnName, index) => ({
                value: String(index + 1),
                text:
                    columnName ||
                    t('settings.devices.eap-settings.eap-config-list.action.import.importing.column.fallback', {
                        index: index + 1,
                    }),
            }));
        }

        return fileColumns.map((_columnName, index) => ({
            value: String(index + 1),
            text: String(index + 1),
        }));
    }, [fileColumns, importFlowData?.columnHeaders, t]);

    const isFieldReadyForImport = (field: EapImportFieldDefinition): boolean => {
        const value = mappings[field.key];

        if (field.controlType === 'file' && value.source === 'FIXED') {
            return !!value.fixedFileName && isValidCertificateFile(value.fixedFileName);
        }

        if (value.source === 'FILE') {
            return !field.required || !!value.fileColumn;
        }

        return !field.required || !!value.fixedValue;
    };

    const importButtonDisabled =
        !fileColumns.length || !selectedDevice || fieldDefinitions.some((field) => !isFieldReadyForImport(field));

    const openConfirmationModal = (params: {
        title: string;
        children: React.ReactNode;
        buttonText: string;
        cancelButtonText: string;
        onConfirm: VoidFunction;
    }) => {
        const { title, children, buttonText, cancelButtonText, onConfirm } = params;

        setIsConfirmationModalOpen(true);
        popupManagerOpen(ConfirmationModalComponent, {
            title,
            children,
            buttonText,
            cancelButtonText,
            actionOnClick: () =>
                new Promise((resolve) => {
                    onConfirm();
                    resolve(true);
                }),
            onClose: () => {
                setIsConfirmationModalOpen(false);
            },
        });
    };

    const openUnsavedDataConfirmationModal = (onConfirm: VoidFunction) => {
        openConfirmationModal({
            title: t(UNSAVED_DATA_MODAL_KEYS.title),
            children: (
                <EapSettingsImportingConfirmationModalContent>
                    <span>{t(UNSAVED_DATA_MODAL_KEYS.descriptionMain)}</span>
                    <span>{t(UNSAVED_DATA_MODAL_KEYS.descriptionSecondary)}</span>
                </EapSettingsImportingConfirmationModalContent>
            ),
            buttonText: t(UNSAVED_DATA_MODAL_KEYS.buttonText),
            cancelButtonText: t(UNSAVED_DATA_MODAL_KEYS.continueEditing),
            onConfirm,
        });
    };

    const onCloseWithUnsavedChangesCheck = () => {
        openUnsavedDataConfirmationModal(onClose);
    };

    const onCloseMissingCertificatesModal = () => {
        if (missingCertificateUploadStatus === 'uploading') {
            setIsAbortUploadModalOpen(true);
            return;
        }

        openUnsavedDataConfirmationModal(() => {
            resetMissingCertificatesFlow();
            setIsMissingCertificatesModalOpen(false);
        });
    };

    if (!importFlowData?.fileContent) {
        return null;
    }

    return (
        <>
            <EapWorkflowOverlayComponent
                visible
                headerTitle={t('settings.devices.eap-settings.eap-config-list.action.import.importing.header')}
                onClose={onCloseWithUnsavedChangesCheck}
                className="show-header"
                footer={[
                    <PocButton
                        key="import-button"
                        onClick={isImporting ? undefined : onImport}
                        disabled={importButtonDisabled || isImporting}
                    >
                        {isImporting ? (
                            <PocProgressSpinner size="xSmall" style={{ display: 'block' }} />
                        ) : (
                            t('settings.devices.eap-settings.eap-config-list.action.import.importing.button.import')
                        )}
                    </PocButton>,
                ]}
            >
                <EapFormContentWrapper>
                    <EapSettingsImportingCard>
                        <EapSettingsImportingAssignSection>
                            <EapSettingsImportingTitle variant="body1">
                                {t(
                                    'settings.devices.eap-settings.eap-config-list.action.import.importing.assign-device.label'
                                )}
                            </EapSettingsImportingTitle>
                            <EapSettingsImportingAssignRow>
                                <Select
                                    name="assign-device"
                                    value={selectedDevice}
                                    placeholder={t(
                                        'settings.devices.eap-settings.eap-config-list.action.import.importing.assign-device.by-serial-number.placeholder'
                                    )}
                                    options={fileColumnOptions}
                                    onChange={(_field, value) =>
                                        setSelectedDevice((getSingleSelectValue(value) as string) || '')
                                    }
                                />
                            </EapSettingsImportingAssignRow>
                        </EapSettingsImportingAssignSection>

                        {fieldDefinitions.map((field) => (
                            <SettingsDevicesEapSettingsImportingFieldComponent
                                key={field.key}
                                field={field}
                                fieldValue={mappings[field.key]}
                                fileColumnOptions={fileColumnOptions}
                                t={t}
                                updateMapping={updateMapping}
                            />
                        ))}
                    </EapSettingsImportingCard>
                </EapFormContentWrapper>
            </EapWorkflowOverlayComponent>

            <SettingsDevicesEapSettingsImportingMissingCertificatesModalComponent
                isOpen={isMissingCertificatesModalOpen && !isAbortUploadModalOpen && !isConfirmationModalOpen}
                missingCertificates={missingCertificates}
                missingCertificateUploadStatus={missingCertificateUploadStatus}
                files={files}
                isImporting={isImporting}
                onLoadFile={onLoadFile}
                onDismissFile={onDismissFile}
                onContinue={onContinue}
                onClose={onCloseMissingCertificatesModal}
                t={t}
            />

            <EapSettingsImportingAbortModal
                isOpen={isAbortUploadModalOpen}
                size="small"
                title={t(
                    'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.abort.title'
                )}
                onClose={() => setIsAbortUploadModalOpen(false)}
                footerOptions={
                    <PocButton variant="primary" size="medium" onClick={onAbortUpload}>
                        {t(
                            'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.abort.button.abort'
                        )}
                    </PocButton>
                }
            >
                <EapSettingsImportingAbortModalDescription>
                    {t(
                        'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.abort.description.question'
                    )}
                </EapSettingsImportingAbortModalDescription>
            </EapSettingsImportingAbortModal>
        </>
    );
};
